import { User } from 'firebase/auth';
import { UserProfile } from './firebase/auth';
import { paymentService } from './payment';

export type SubscriptionTier = 'free' | 'pro';

export interface SubscriptionFeatures {
  canSavePrompts: boolean;
  requiresOwnApiKeys: boolean;
  maxPromptsSaved: number;
  canAccessDashboard: boolean;
  canExportPrompts: boolean;
  canSharePrompts: boolean;
  canUseTemplates: boolean;
  canUseAIFeatures: boolean;
}

export const SUBSCRIPTION_FEATURES: Record<SubscriptionTier, SubscriptionFeatures> = {
  free: {
    canSavePrompts: false,
    requiresOwnApiKeys: true,
    maxPromptsSaved: 0,
    canAccessDashboard: true, // Can view dashboard but limited features
    canExportPrompts: true,
    canSharePrompts: false,
    canUseTemplates: true,
    canUseAIFeatures: true, // But requires own API keys
  },
  pro: {
    canSavePrompts: true,
    requiresOwnApiKeys: false,
    maxPromptsSaved: -1, // Unlimited
    canAccessDashboard: true,
    canExportPrompts: true,
    canSharePrompts: true,
    canUseTemplates: true,
    canUseAIFeatures: true,
  },
};

export const SUBSCRIPTION_BENEFITS = {
  pro: [
    '✅ Save unlimited prompts to cloud',
    '✅ No API keys required',
    '✅ Access to app\'s premium AI models',
    '✅ Full dashboard with analytics',
    '✅ Share prompts with community',
    '✅ Priority support',
    '✅ Early access to new features',
  ],
  free: [
    '✅ Access all prompt templates',
    '✅ Copy & download prompts',
    '✅ Basic dashboard access',
    '❌ Cannot save prompts to cloud',
    '❌ Must provide own API keys',
    '❌ Limited to local storage only',
  ],
};

class SubscriptionService {
  /**
   * Get user's subscription tier
   */
  getUserTier(profile: UserProfile | null): SubscriptionTier {
    if (!profile) return 'free';
    return profile.subscription || 'free';
  }

  /**
   * Check if user can perform an action based on their subscription
   */
  canUserPerformAction(
    profile: UserProfile | null,
    action: keyof SubscriptionFeatures
  ): boolean {
    const tier = this.getUserTier(profile);
    return SUBSCRIPTION_FEATURES[tier][action];
  }

  /**
   * Check if user needs to provide their own API keys
   */
  requiresOwnApiKeys(profile: UserProfile | null): boolean {
    return this.canUserPerformAction(profile, 'requiresOwnApiKeys');
  }

  /**
   * Check if user can save prompts
   */
  canSavePrompts(profile: UserProfile | null): boolean {
    return this.canUserPerformAction(profile, 'canSavePrompts');
  }

  /**
   * Get subscription features for a tier
   */
  getFeatures(tier: SubscriptionTier): SubscriptionFeatures {
    return SUBSCRIPTION_FEATURES[tier];
  }

  /**
   * Get benefits list for display
   */
  getBenefits(tier: SubscriptionTier): string[] {
    return SUBSCRIPTION_BENEFITS[tier as keyof typeof SUBSCRIPTION_BENEFITS] || [];
  }

  /**
   * Check if user is on Pro plan
   */
  isPro(profile: UserProfile | null): boolean {
    return this.getUserTier(profile) === 'pro';
  }

  /**
   * Upgrade to Pro plan via Stripe
   */
  async upgradeToProPlan(user: User, planType: 'pro_monthly' | 'pro_yearly' = 'pro_monthly'): Promise<void> {
    try {
      // Process payment with Stripe
      await paymentService.processSubscription(user, planType);
    } catch (error) {
      console.error('Error upgrading to Pro:', error);
      throw error;
    }
  }

  /**
   * Cancel Pro subscription
   */
  async cancelProPlan(user: User): Promise<void> {
    try {
      await paymentService.cancelSubscription(user);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  }

  /**
   * Get billing portal URL
   */
  async getBillingPortalUrl(user: User): Promise<string> {
    try {
      return await paymentService.getCustomerPortalUrl(user);
    } catch (error) {
      console.error('Error getting billing portal:', error);
      throw error;
    }
  }

  /**
   * Format subscription tier for display
   */
  formatTierName(tier: SubscriptionTier): string {
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  }

  /**
   * Get subscription badge color
   */
  getTierColor(tier: SubscriptionTier): string {
    return tier === 'pro' ? 'gradient-to-r from-amber-500 to-orange-500' : 'gradient-to-r from-gray-500 to-gray-600';
  }

  /**
   * Check if action requires upgrade
   */
  isUpgradeRequired(profile: UserProfile | null, action: keyof SubscriptionFeatures): boolean {
    const currentTier = this.getUserTier(profile);
    const canPerform = SUBSCRIPTION_FEATURES[currentTier][action];
    const proCanPerform = SUBSCRIPTION_FEATURES.pro[action];
    
    return !canPerform && proCanPerform;
  }
}

export const subscriptionService = new SubscriptionService();
export default subscriptionService;